/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { UserAtom } from "../../../atoms/UserAtom";
import { SettingsTab } from "../../../pages/settings";
import { nanoidForFooterAndAlias } from "../../NewEmail/Modals/SavedSearch/FooterSearchModal";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { AllIntegrationsAtom } from "./Integrations";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const TitleWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  margin-bottom: 24px;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
    border-bottom: unset;
    padding-bottom: unset;
    margin-bottom: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;
//!

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

interface StripeProps {
  handleSelected: (vle: SettingsTab) => void;
}

export default function Stripe({
  handleSelected,
}: StripeProps): JSX.Element | null {
  const user = useRecoilValue(UserAtom);
  const [apiKey, setApiKey] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const setAllIntegrations = useSetRecoilState(AllIntegrationsAtom);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!apiKey) {
      toast("Fill the API Key field", {
        type: "error",
      });

      return;
    }

    if (!user) return;

    setIsLoading(true);

    if (apiKey) {
      const isCreated = await IntegrationAPI.create({
        integration: {
          userUid: user.userUid,
          args: {
            apiKey,
            integrationId: nanoidForFooterAndAlias(),
          },
          type: "STRIPE",
        },
      });

      if (isCreated) {
        let integrations = await IntegrationAPI.getAll();

        setAllIntegrations(integrations);

        setTimeout(() => {
          handleSelected(SettingsTab.STRIPEPRODUCTS);
        }, 100);
        return;
      }

      toast("Error on verifying ThriveCart API Key. Check it and try again.", {
        type: "error",
      });
      setIsLoading(false);
    }

    return;
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleSubmit(event);
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [apiKey]);

  return (
    <Container>
      <TitleWrapper>
        <TextWrapper>
          <MainTitle>Stripe Integration</MainTitle>
          <MainText>
            Please provide the following personal information in the area below.
            These details will be used for account login and authentication.
          </MainText>
        </TextWrapper>

        <img src="./images/stripe.png" alt="stripe" />
      </TitleWrapper>

      <TextWrapper>
        <MainTitle>API Keys</MainTitle>
        <MainText>
          You can locate your Stripe API key under the Account panel.
        </MainText>
      </TextWrapper>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>API Key</SecondaryTitle>
            <MainText>Your private API Key.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 237px;
            `}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Put your API Key here"
          />
        </SpaceBetween>
      </FormContent>

      <FooterColumn>
        <StyledButton
          isLoading={isLoading}
          onClick={handleSubmit}
          disable={!apiKey}
        >
          Connect with Stripe
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
